/**
 * Segment Builder — modal for creating segments with visual filter conditions.
 */

import { useState, useCallback } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import type { FilterCondition, FilterGroup } from '@/types/crm'
import { SEGMENT_FILTER_FIELDS, SEGMENT_OPERATORS } from '@/types/crm'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useToast,
} from '@/components/ui'

interface SegmentBuilderProps {
  onClose: () => void
}

const EMPTY_CONDITION: FilterCondition = { field: 'channel', op: 'eq', value: '' }
const NO_VALUE = 'unspecified'

export function SegmentBuilder({ onClose }: SegmentBuilderProps) {
  const { createSegment } = useCRM()
  const { addToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#3B82F6')
  const [rootOperator, setRootOperator] = useState<'and' | 'or'>('and')
  const [conditions, setConditions] = useState<FilterCondition[]>([{ ...EMPTY_CONDITION }])
  const [previewCount, setPreviewCount] = useState<number | null>(null)

  const addCondition = () => {
    setConditions([...conditions, { ...EMPTY_CONDITION }])
  }

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index))
  }

  const updateCondition = (index: number, updates: Partial<FilterCondition>) => {
    const newConditions = [...conditions]
    newConditions[index] = { ...newConditions[index], ...updates }

    // Reset value and op when field changes
    if (updates.field) {
      const fieldDef = SEGMENT_FILTER_FIELDS.find((f) => f.value === updates.field)
      const ops = SEGMENT_OPERATORS[fieldDef?.type || 'text'] || []
      newConditions[index].op = ops[0]?.value || 'eq'
      newConditions[index].value = ''
    }

    setConditions(newConditions)
  }

  const buildFilterGroup = (): FilterGroup => ({
    operator: rootOperator,
    conditions: conditions.filter((c) => c.field && c.op).map((c) => ({
      field: c.field,
      op: c.op,
      value: c.op === 'is_set' || c.op === 'is_not_set' ? undefined : c.value,
    })),
  })

  const handlePreview = useCallback(async () => {
    try {
      const { apiClient } = await import('@/services/apiClient')
      const res = await apiClient.fetch('/api/crm/segments', {
        method: 'POST',
        body: JSON.stringify({
          name: '__preview__',
          filters: buildFilterGroup(),
        }),
      })
      if (res.ok) {
        const data = await res.json()
        setPreviewCount(data.cached_count ?? 0)
        // Delete the preview segment
        if (data.id) {
          await apiClient.fetch(`/api/crm/segments/${data.id}`, { method: 'DELETE' })
        }
      }
    } catch {
      // Preview is best-effort
    }
  }, [conditions, rootOperator])

  const handleSubmit = async () => {
    if (!name.trim() || conditions.length === 0) return

    setIsSubmitting(true)
    const success = await createSegment({
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      filters: buildFilterGroup(),
    })
    setIsSubmitting(false)
    if (success) {
      addToast({ type: 'success', title: 'Segment created' })
      onClose()
    } else {
      addToast({ type: 'error', title: 'Failed to create segment', description: 'Check your connection and try again.' })
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent
        size="md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <ModalHeader><ModalTitle>Create Segment</ModalTitle></ModalHeader>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
        <ModalBody className="space-y-4">
          {/* Name + Color */}
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., High-Value Customers"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Color</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-12 rounded-md border border-input cursor-pointer"
              />
            </div>
          </div>

          {/* Description */}
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />

          {/* Filter operator */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Match</span>
            <Select value={rootOperator} onValueChange={(v) => setRootOperator(v as 'and' | 'or')}>
              <SelectTrigger size="sm" className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="and">ALL</SelectItem>
                <SelectItem value="or">ANY</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">of these conditions:</span>
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            {conditions.map((condition, index) => (
              <ConditionRow
                key={index}
                condition={condition}
                onChange={(updates) => updateCondition(index, updates)}
                onRemove={() => removeCondition(index)}
                canRemove={conditions.length > 1}
              />
            ))}
          </div>

          {/* Add condition */}
          <Button type="button" variant="outline" size="sm" onClick={addCondition}>
            <Plus className="w-3.5 h-3.5" />
            Add condition
          </Button>

          {/* Preview */}
          <div className="flex items-center gap-3 py-2 px-3 bg-muted rounded-lg">
            <Button type="button" variant="outline" size="sm" onClick={handlePreview}>
              Preview
            </Button>
            {previewCount !== null && (
              <span className="text-sm text-muted-foreground">
                {previewCount} contact{previewCount !== 1 ? 's' : ''} match
              </span>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || conditions.length === 0} isLoading={isSubmitting}>
            Create Segment
          </Button>
        </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

// ── Condition Row ────────────────────────────────────────────────────────────

function ConditionRow({ condition, onChange, onRemove, canRemove }: {
  condition: FilterCondition
  onChange: (updates: Partial<FilterCondition>) => void
  onRemove: () => void
  canRemove: boolean
}) {
  const fieldDef = SEGMENT_FILTER_FIELDS.find((f) => f.value === condition.field)
  const fieldType = fieldDef?.type || 'text'
  const operators = SEGMENT_OPERATORS[fieldType] || SEGMENT_OPERATORS.text
  const needsValue = condition.op !== 'is_set' && condition.op !== 'is_not_set'

  return (
    <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
      {/* Field */}
      <Select value={condition.field} onValueChange={(v) => onChange({ field: v })}>
        <SelectTrigger size="sm" className="min-w-[120px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SEGMENT_FILTER_FIELDS.map((f) => (
            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator */}
      <Select value={condition.op} onValueChange={(v) => onChange({ op: v })}>
        <SelectTrigger size="sm" className="min-w-[130px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value */}
      {needsValue && (
        <>
          {fieldType === 'select' && fieldDef && 'options' in fieldDef ? (
            <Select
              value={(condition.value as string) || NO_VALUE}
              onValueChange={(v) => onChange({ value: v === NO_VALUE ? '' : v })}
            >
              <SelectTrigger size="sm" className="flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_VALUE}>Select...</SelectItem>
                {(fieldDef.options as readonly string[]).map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : fieldType === 'boolean' ? (
            <Select
              value={String(condition.value ?? '')}
              onValueChange={(v) => onChange({ value: v === 'true' })}
            >
              <SelectTrigger size="sm" className="flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex-1 min-w-[80px]">
              <Input
                type={fieldType === 'number' || fieldType === 'date' ? 'number' : 'text'}
                value={String(condition.value ?? '')}
                onChange={(e) => {
                  const val = fieldType === 'number' || fieldType === 'date' ? Number(e.target.value) : e.target.value
                  onChange({ value: val })
                }}
                placeholder={fieldType === 'date' ? 'days' : 'value'}
              />
            </div>
          )}
        </>
      )}

      {/* Remove */}
      {canRemove && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Remove condition"
          onClick={onRemove}
        >
          <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      )}
    </div>
  )
}
